/**
 * special_condition 编辑器：targeting_id=`、`-joined 策略码，ext_info=JSON
 * {code: {excluded_custom: [ids], custom_target: 0|1}}。
 * custom_target 表示该特殊策略是否支持「正向人群包为空」（1=支持/可为空，0=不支持）。
 * 每个策略码一行（码只读+删除）+ excluded_custom ids chips + custom_target 复选；
 * 「添加策略码」需填码（新码默认 custom_target=0）。编辑 excluded_custom 时保留
 * 该码的 custom_target（不覆盖丢失）。
 */
import { useState } from "react";
import type { EditorProps } from "./types";
import { IdChips } from "./IdChips";
import { joinDy, parseJsonExt, splitDy, stringifyJsonExt } from "../../utils/redlineHelpers";

interface CondEntry {
  excluded_custom?: number[] | string[];
  custom_target?: number;
}
type CondMap = Record<string, CondEntry>;

export function SpecialConditionEditor({ item, onChange }: EditorProps) {
  const codes = splitDy(item.targeting_id);
  const ext = parseJsonExt<CondMap>(item.ext_info, {});

  const [codeDraft, setCodeDraft] = useState("");

  function serialize(nextCodes: string[], nextExt: CondMap) {
    onChange({
      targeting_id: joinDy(nextCodes),
      ext_info: stringifyJsonExt(nextExt),
    });
  }

  function setIdsFor(code: string, ids: string[]) {
    const prev = ext[code] ?? {};
    serialize(codes, { ...ext, [code]: { ...prev, excluded_custom: ids } });
  }

  function setCustomTarget(code: string, val: number) {
    const prev = ext[code] ?? {};
    serialize(codes, { ...ext, [code]: { ...prev, custom_target: val } });
  }

  function removeCode(code: string) {
    const nextCodes = codes.filter((c) => c !== code);
    const next: CondMap = { ...ext };
    delete next[code];
    serialize(nextCodes, next);
  }

  function addCode() {
    const code = codeDraft.trim();
    if (!code || codes.includes(code)) {
      setCodeDraft("");
      return;
    }
    serialize([...codes, code], {
      ...ext,
      [code]: { excluded_custom: [], custom_target: 0 },
    });
    setCodeDraft("");
  }

  return (
    <div className="space-y-2">
      {codes.map((code) => {
        const idsRaw = ext[code]?.excluded_custom ?? [];
        const ids = idsRaw.map((x) => String(x));
        const customTarget = ext[code]?.custom_target ?? 0;
        return (
          <div key={code} className="rl-subbox">
            <div className="mb-1.5 flex items-center gap-2">
              <span className="whitespace-nowrap text-[12px] text-text-muted">RTA策略ID：</span>
              <span className="leo-tag is-info">{code}</span>
              <button
                type="button"
                className="text-[11px] text-text-muted hover:text-red-400"
                onClick={() => removeCode(code)}
              >
                删除码
              </button>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-1.5 whitespace-nowrap text-[12px] text-text-muted">负向人群包id</span>
              <div className="min-w-0 flex-1">
                <IdChips
                  ids={ids}
                  onChange={(next) => setIdsFor(code, next)}
                  placeholder="该码需剔除的人群包 id，回车添加"
                />
              </div>
            </div>
            <label className="mt-2 flex items-center gap-2 text-[12px] text-text-muted">
              <input
                type="checkbox"
                className="accent-[#ff6b35]"
                checked={customTarget === 1}
                onChange={(e) => setCustomTarget(code, e.target.checked ? 1 : 0)}
              />
              支持正向人群包为空（custom_target=1）
            </label>
          </div>
        );
      })}
      <div className="flex items-center gap-2 border-t border-line-subtle pt-2">
        <input
          type="text"
          className="leo-input w-40"
          placeholder="新策略码（如 ABCDE）"
          value={codeDraft}
          onChange={(e) => setCodeDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCode();
            }
          }}
        />
        <button type="button" className="leo-btn-ghost" onClick={addCode}>
          添加策略码
        </button>
      </div>
    </div>
  );
}
